import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { 
  Sparkles, 
  Copy, 
  Check,
  Loader2,
  FileText
} from "lucide-react";

const APPLIANCES = [
  "Washer",
  "Dryer", 
  "Refrigerator",
  "Dishwasher",
  "Oven/Range",
  "Microwave",
  "Freezer",
  "Ice Maker",
  "Garbage Disposal",
  "Cooktop",
  "Range Hood",
  "Wine Cooler",
  "Trash Compactor",
  "Other"
];

export default function DiagnosticGenerator() {
  const [appliance, setAppliance] = useState("");
  const [issue, setIssue] = useState("");
  const [partNeeded, setPartNeeded] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [diagnostic, setDiagnostic] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateDiagnostic = async () => {
    if (!appliance) {
      toast.error("Please select an appliance");
      return;
    }
    if (!issue) {
      toast.error("Please describe the issue");
      return;
    }

    setLoading(true);
    setDiagnostic("");
    
    try {
      const response = await axios.post("/diagnostic/generate", {
        appliance,
        issue,
        part_needed: partNeeded || null,
        additional_notes: additionalNotes || null
      });
      
      setDiagnostic(response.data.diagnostic);
      toast.success("Diagnostic generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate diagnostic");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(diagnostic);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const clearForm = () => {
    setAppliance("");
    setIssue("");
    setPartNeeded("");
    setAdditionalNotes("");
    setDiagnostic("");
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full mb-4">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">AI Pro Diagnostic</span>
        </div>
        <p className="text-slate-600">
          Generate professional diagnostic reports for invoices
        </p>
      </div>

      {/* Input Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Diagnostic Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Appliance Selection */}
          <div>
            <Label>Appliance Type *</Label>
            <select
              value={appliance}
              onChange={(e) => setAppliance(e.target.value)}
              className="w-full h-10 px-3 mt-1 border border-slate-300 rounded-md"
            >
              <option value="">Select appliance...</option>
              {APPLIANCES.map((app) => (
                <option key={app} value={app}>{app}</option>
              ))}
            </select>
          </div>

          {/* Issue Description */}
          <div>
            <Label>Issue Found *</Label>
            <textarea
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
              placeholder="Describe what you found... (e.g., 'not draining, pump making noise, found debris in drain pump')"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md min-h-[80px]"
            />
          </div>

          {/* Part Needed */}
          <div>
            <Label>Part Needed (if any)</Label>
            <Input
              value={partNeeded}
              onChange={(e) => setPartNeeded(e.target.value)}
              placeholder="e.g., Drain pump, Door seal, Control board..."
            />
          </div>

          {/* Additional Notes */}
          <div>
            <Label>Additional Notes (optional)</Label>
            <Input
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Any other details..."
            />
          </div>

          {/* Generate Button */}
          <Button 
            onClick={generateDiagnostic}
            disabled={loading}
            className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Generate Professional Diagnostic
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Generated Diagnostic */}
      {diagnostic && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg text-green-800">
                📋 Professional Diagnostic
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyToClipboard}
                  className="border-green-300 hover:bg-green-100"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearForm}
                >
                  Clear
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">
                {diagnostic}
              </p>
            </div>
            <p className="text-xs text-green-700 mt-3">
              💡 This diagnostic uses professional language suitable for customer invoices. 
              It describes what needs to be done without stating work has been completed.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="bg-slate-50">
        <CardContent className="pt-4">
          <p className="text-sm text-slate-600">
            <strong>💡 Tips for best results:</strong>
          </p>
          <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
            <li>Be specific about what you found during inspection</li>
            <li>Include symptoms the customer reported</li>
            <li>Mention the part name if replacement is needed</li>
            <li>The AI will write in future tense ("will need to be replaced")</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
